import {
  Component,
  DestroyRef,
  inject,
  HostBinding,
  OnInit,
} from '@angular/core';
import { RouteConstants } from '../../constants/route-constants';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { IconName } from '../../icons/icon-map';
import { TranslatePipe } from '@ngx-translate/core';
import { IconComponent } from '../../icons/icon.component';
import { MainNavigationService } from './main-navigation.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MainNavigationTriggerComponent } from './main-navigation-trigger/main-navigation-trigger.component';
import { UserService } from '@sso/ld-sso-with-oauth2-oidc';
import { Role } from '../../../core/enums/role.enum';
import { LogoComponent } from '../logo/logo.component';

@Component({
  selector: 'app-main-navigation',
  imports: [
    RouterLinkActive,
    RouterLink,
    IconComponent,
    TranslatePipe,
    LogoComponent,
    MainNavigationTriggerComponent,
  ],
  templateUrl: './main-navigation.component.html',
  styleUrl: './main-navigation.component.scss',
})
export class MainNavigationComponent implements OnInit {
  private _mainNavigationService = inject(MainNavigationService);
  private _userService = inject(UserService<Role>);

  private destroyRef = inject(DestroyRef);

  isResponsiveMenuOpen = false;
  userRoles: Role[] = [];

  @HostBinding('class.open')
  get openClass() {
    return this.isResponsiveMenuOpen;
  }

  private primaryNav: NavigationData[] = [
    {
      labelKey: 'navigation.dashboard.label',
      icon: 'ic_dashboard',
      link: RouteConstants.routeDashboard,
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    {
      labelKey: 'navigation.element.label',
      icon: 'ic_unknown_device',
      roles: [Role.RM_USER, Role.RM_ADMIN],
      sub: [
        {
          icon: 'ic_device_group',
          labelKey: 'navigation.deviceGroup.label',
          link: RouteConstants.routeDeviceGroup,
        },
        {
          icon: 'ic_ventile',
          labelKey: 'navigation.device.label',
          link: RouteConstants.routeDevice,
        },
        {
          icon: 'ic_pump',
          labelKey: 'navigation.pump.label',
          link: RouteConstants.routePump,
        },
        {
          icon: 'ic_cistern',
          labelKey: 'navigation.cistern.label',
          link: RouteConstants.routeCistern,
        },
      ],
    },
    {
      labelKey: 'navigation.rule.label',
      icon: 'ic_rules',
      link: RouteConstants.routeRule,
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    {
      labelKey: 'navigation.statistics.label',
      icon: 'ic_analyse',
      link: RouteConstants.routeStatistics,
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    {
      labelKey: 'navigation.weather.label',
      icon: 'ic_sun',
      link: RouteConstants.routeWeather,
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    // {
    //   labelKey: 'navigation.camera.label',
    //   icon: 'ic_camera',
    //   link: RouteConstants.routeCamera
    // },
    {
      labelKey: 'navigation.map.label',
      icon: 'ic_map',
      link: RouteConstants.routeMap,
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    // {
    //   labelKey: 'navigation.organization.label',
    //   icon: 'ic_organisation',
    //   link: RouteConstants.routeOrganization
    // },
    {
      labelKey: 'navigation.demo.label',
      icon: 'ic_dashboard',
      link: RouteConstants.routeDemo,
      roles: [Role.RM_DEMO, Role.RM_DEMO_GROUP],
    },
    {
      labelKey: 'navigation.actionLog.label',
      icon: 'ic_rule',
      link: RouteConstants.actionLog,
      roles: [Role.RM_ADMIN],
    },
  ];
  visiblePrimaryNav: NavigationData[] = [];

  // todo include in another release
  secondaryNav: NavigationData[] = [
    {
      labelKey: 'navigation.discover.label',
      icon: 'ic_discover',
      link: RouteConstants.routeDiscover,
    },
    {
      labelKey: 'navigation.settings.label',
      icon: 'ic_settings',
      link: RouteConstants.routeSettings,
    },
    {
      labelKey: 'navigation.help.label',
      icon: 'ic_help',
      link: RouteConstants.routeHelp,
    },
  ];

  tertiaryNav: NavigationData[] = [
    {
      labelKey: 'navigation.imprint.label',
      icon: null,
      link: RouteConstants.routeImprint,
    },
    {
      labelKey: 'navigation.privacy.label',
      icon: null,
      link: RouteConstants.routePrivacy,
    },
    {
      labelKey: 'navigation.terms.label',
      icon: null,
      link: RouteConstants.routeTerms,
    },
  ];

  ngOnInit() {
    this._mainNavigationService.menuOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => (this.isResponsiveMenuOpen = v));

    this._userService
      .getUserInfo$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        const roles = user.info?.roles ?? [];
        this.userRoles = roles.filter((role): role is Role =>
          Object.values(Role).includes(role as Role),
        );
        this.visiblePrimaryNav = this.primaryNav.filter(
          (item) =>
            !item.roles?.length ||
            item.roles.some((role) => this.userRoles.includes(role)),
        );
      });

    // todo close when route changing
  }

  onCloseMenu() {
    this._mainNavigationService.setMenuState(false);
  }

  getMainLink(item: NavigationData): string | undefined {
    return item.link ?? item.sub?.[0]?.link;
  }

  getSubItems(item: NavigationData): NavigationData[] {
    return item.sub ?? [];
  }
}

export type NavigationData = {
  labelKey: string;
  icon?: IconName | null;
  roles?: Role[];
  link?: string;
  action?: () => void;
  sub?: Array<NavigationData>;
};
